import { z } from 'zod';
import type { DataPort } from '../../ports/data-port.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import { newId } from '../../domain/id.ts';
import { AddressSchema, DateOnlySchema } from '../../domain/entities/common.ts';
import { DEFAULT_PORTAL_MODULES, JobStatusSchema, type Job } from '../../domain/entities/job.ts';

/**
 * A job cannot end before it starts.
 *
 * Shared by create and update because it is the same nonsense in both
 * directions, and a rule enforced on only one of them is a rule somebody
 * escapes by saving twice.
 *
 * Calendar dates are ISO `YYYY-MM-DD` strings, which compare correctly as
 * strings — no parsing, and no time zone to get wrong.
 */
export function endNotBeforeStart(
  value: {
    readonly startDate?: string | undefined;
    readonly projectedEndDate?: string | undefined;
    readonly actualEndDate?: string | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  const start = value.startDate;
  if (!start) return;
  for (const field of ['projectedEndDate', 'actualEndDate'] as const) {
    const end = value[field];
    if (end && end < start) {
      ctx.addIssue({
        code: 'custom',
        path: [field],
        message: 'This cannot be before the start date',
      });
    }
  }
}

/**
 * What a person actually fills in. Everything else on a Job is derived or
 * defaulted here, so a form never has to know about audit fields, portal
 * toggles or empty team arrays.
 */
export const CreateJobInputSchema = z
  .object({
    name: z.string().trim().min(1, 'Give the job a name').max(200),
    prefix: z
      .string()
      .trim()
      .min(1, 'Add a prefix')
      .max(8, 'Keep the prefix to 8 characters or fewer')
      // The prefix becomes part of every document number on the job
      // (GLN-0001), so it is constrained to characters that stay readable in a
      // filename, a URL and a printed invoice.
      .regex(/^[A-Za-z0-9-]+$/, 'Use letters, numbers and hyphens only')
      .transform((value) => value.toUpperCase()),
    status: JobStatusSchema.default('open'),
    address: AddressSchema,
    startDate: DateOnlySchema.optional(),
    projectedEndDate: DateOnlySchema.optional(),
    contractType: z.enum(['fixed', 'openBook']).default('fixed'),
  })
  .superRefine(endNotBeforeStart);

export type CreateJobInput = z.input<typeof CreateJobInputSchema>;

export interface CreateJobContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly uid: string;
  /** Falls back to the company's zone when the job does not set its own. */
  readonly companyTimeZone: string;
}

export class JobValidationError extends Error {
  readonly fieldErrors: Record<string, string>;

  constructor(fieldErrors: Record<string, string>) {
    // The message repeats the field detail so a log line or a stack trace is
    // self-explanatory; fieldErrors is what a form binds to.
    const summary = Object.entries(fieldErrors)
      .map(([field, message]) => `${field}: ${message}`)
      .join('; ');
    super(`The job details need attention — ${summary}`);
    this.name = 'JobValidationError';
    this.fieldErrors = fieldErrors;
  }
}

/**
 * Create a job.
 *
 * Lives here rather than in the form so the rules are testable without a
 * browser, and so the mobile app and any future importer get the same
 * behaviour rather than a second, slightly different implementation.
 */
export async function createJob(context: CreateJobContext, input: CreateJobInput): Promise<Job> {
  const parsed = CreateJobInputSchema.safeParse(input);
  if (!parsed.success) {
    const fieldErrors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      const key = issue.path.join('.');
      fieldErrors[key] ??= issue.message;
    }
    throw new JobValidationError(fieldErrors);
  }

  const now = context.clock.now().toISOString();
  const job: Job = {
    id: newId(),
    companyId: context.companyId,
    name: parsed.data.name,
    prefix: parsed.data.prefix,
    status: parsed.data.status,
    address: parsed.data.address,
    timeZone: context.companyTimeZone,
    ownerContactIds: [],
    projectManagerUids: [context.uid],
    internalUids: [context.uid],
    subContactIds: [],
    startDate: parsed.data.startDate,
    projectedEndDate: parsed.data.projectedEndDate,
    contractType: parsed.data.contractType,
    tags: [],
    // A new schedule starts offline so a half-built plan is not pushed to
    // subs and owners before anyone has reviewed it.
    scheduleOnline: false,
    portalModules: DEFAULT_PORTAL_MODULES,
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  await context.data.collection<Job>({ companyId: context.companyId }, 'jobs').create(job);
  return job;
}
